/**
 * Annual **series** layers — the catalogue half of S3's time slider.
 *
 * §D5 says a raster row carries `epoch` (the year it *is*) or `epochs` (the years a series
 * row can step to, each naming the layer id that draws it). Those two fields are the whole
 * convention: nothing here invents a year, a tile URL or a product. The slider offers the
 * years the harvester actually published, which for the land-cover stand-in is 2017–2024 —
 * eight, not the nine the client's "2017–2025" asks for, because 2024 is where the bucket
 * ends (docs/NOTES.md).
 *
 * ## Why the epoch rows are hidden from the datasets list
 *
 * `lc_annual` and its eight `lcaYYYY` rows are nine catalogue rows for one dataset. Listing
 * all nine under Land Use would make the reader pick a year from a dropdown *and* from a
 * slider, with nothing saying which won. So the pane lists the **series** row and the
 * slider addresses the years — `epochMemberIds` is what the pane filters on, and
 * `seriesFor` is what keeps the pane's own select reading "Land cover — annual series"
 * while the map is drawing 2019.
 *
 * The epoch rows are not hidden from anything else: §H5's change tool still addresses them
 * by id, and they are still in `/layers` for every picker that wants a single year.
 */
import type { Layer, LayerEpoch } from '../../lib/api';
import i18n from '../../lib/i18n';
// `NDVI_SERIES` has to live in `lib/state.ts`, not here: `state.ts` imports it, and `api.ts`
// (which this module imports `Layer`/`LayerEpoch` from) already imports from `state.ts` —
// so `state.ts` importing this module back would be the cycle. Re-exported so every other
// caller can keep reading it from `features/map/epochs.ts`, where the rest of the temporal
// helpers live.
export { NDVI_SERIES } from '../../lib/state';

/** Every layer that carries a published series (`config.epochs`), catalogue order. */
export function seriesLayers(layers: readonly Layer[] | undefined): Layer[] {
  return (layers ?? []).filter((l) => (l.epochs?.length ?? 0) > 0);
}

/**
 * The ids that are a *year of* some series — the rows the datasets list leaves out.
 *
 * Built from the series rows rather than from `epoch != null`, deliberately: a class
 * raster can declare an epoch without belonging to a series (the two WorldCover COGs do),
 * and hiding those would take two real datasets off the list to tidy up nine.
 */
export function epochMemberIds(layers: readonly Layer[] | undefined): Set<string> {
  const ids = new Set<string>();
  for (const series of seriesLayers(layers)) {
    for (const epoch of series.epochs ?? []) {
      // A date series' epochs all carry `layer === series.id` (Task 6) — that is the
      // series itself, not a separate member row, so it must stay in `#rasterSelect`.
      if (epoch.layer !== series.id) ids.add(epoch.layer);
    }
  }
  return ids;
}

/**
 * The series row this layer id belongs to — itself if it is one, else the series that
 * names it as a year, else `null`.
 *
 * This is what makes the slider survive its own use: picking 2019 puts `lca2019` on the
 * map, and the next render has to find its way back to `lc_annual` to know which years are
 * still on offer.
 */
export function seriesFor(
  layers: readonly Layer[] | undefined,
  id: string | null,
): Layer | null {
  if (!id) return null;
  const rows = seriesLayers(layers);
  return (
    rows.find((l) => l.id === id) ??
    rows.find((l) => (l.epochs ?? []).some((e) => e.layer === id)) ??
    null
  );
}

/**
 * Which year of `series` is drawn right now.
 *
 * The series row itself is bound to the **newest** published year (`apps.datasets.seed
 * .lc_annual`: it borrows that year's dataset, so `config.cog` resolves to that year's
 * COG), so a map showing `lc_annual` is showing the last epoch and the slider says so.
 * An id that is neither the series nor one of its years is `-1`, which every caller reads
 * as "the slider is not about this layer".
 *
 * Task 6: a **date series** (`ndvi_terra`) draws every epoch through the series' own id —
 * `drawn === series.id` is true whichever epoch is on screen — so the legacy rule above
 * cannot tell them apart on its own. `epochOn` (optional, defaulting to "nothing picked") is
 * read only for such a series, and the index returned is `drawnEpochOf`'s: the epoch named in
 * `epochOn[series.id]`, or the newest when nothing is. A legacy series ignores the argument
 * entirely, so every existing call site (which passes none) is unaffected.
 */
export function epochIndexOf(
  series: Layer,
  drawn: string | null,
  epochOn?: Record<string, string>,
): number {
  const epochs = series.epochs ?? [];
  if (!epochs.length) return -1;
  if (isDateSeries(series)) {
    if (drawn !== series.id) return -1;
    const epoch = drawnEpochOf(series, new Set([series.id]), epochOn ?? {});
    return epoch ? epochs.indexOf(epoch) : -1;
  }
  if (drawn === series.id) return epochs.length - 1;
  return epochs.findIndex((e) => e.layer === drawn);
}

/**
 * True for a **date series** — every epoch draws through the series' own id, rather than each
 * epoch being a separate catalogue row (`lc_annual`'s `lca2019`, `lca2024`, …). This is the
 * shape Task 6 adds: `ndvi_terra`'s epochs each carry `layer: 'ndvi_terra'` and a `date`, and
 * the reader picks among them with `epochOn` rather than by switching which id is in
 * `layersOn`. An empty `epochs` is not a date series (nothing to be true of yet).
 */
export function isDateSeries(series: Layer): boolean {
  const epochs = series.epochs ?? [];
  return epochs.length > 0 && epochs.every((e) => e.layer === series.id);
}

/** An epoch's ISO date — `date` when the server sent one, else `${year}-01-01`. */
export function epochDate(epoch: LayerEpoch): string {
  return epoch.date ?? `${epoch.year}-01-01`;
}

/**
 * `series`' epochs falling in `year` — oldest first, the shape `epochsOf` already returns.
 * `null` is "every year", the same convention `epochLabel`'s year field and the pane's
 * "all years" option use elsewhere in the catalogue.
 */
export function epochsForYear(series: Layer, year: number | null): LayerEpoch[] {
  const epochs = series.epochs ?? [];
  if (year === null) return epochs;
  return epochs.filter((e) => Number(epochDate(e).slice(0, 4)) === year);
}

/**
 * The epoch to draw for an arbitrary calendar day — the latest one at or before `isoDate`,
 * falling back to the very first epoch when the date predates the series entirely (there is
 * no "nothing published yet" state a map can show; the oldest epoch is the honest answer).
 * `epochs` is assumed sorted oldest-first, as every `/layers` series row already is.
 */
export function snapEpoch(epochs: LayerEpoch[], isoDate: string): LayerEpoch | null {
  if (!epochs.length) return null;
  let best: LayerEpoch | null = null;
  for (const epoch of epochs) {
    const d = epochDate(epoch);
    if (d <= isoDate && (!best || epochDate(best) < d)) best = epoch;
  }
  return best ?? epochs[0] ?? null;
}

/**
 * Which epoch of `series` is drawn right now — the one fact `RasterLayers` needs to pick a
 * tile URL and Task 7's controls need to pick a selection.
 *
 * **Date series** (`ndvi_terra`): the epoch is not which id is in `layersOn` (there is only
 * ever one, the series' own) but `epochOn[series.id]` — the ISO date the reader last picked.
 * Matched by date rather than by array position, so a stale index from a shorter/longer
 * epoch list never points at the wrong pixel; an unmatched or absent pick falls back to the
 * newest epoch, exactly as drawing the series id itself always has for the legacy shape.
 *
 * **Legacy series** (`lc_annual`): unchanged — the drawn id is whichever member of
 * `layersOn` is the series itself or one of its per-year rows, read through `epochIndexOf`.
 */
export function drawnEpochOf(
  series: Layer,
  layersOn: ReadonlySet<string>,
  epochOn: Record<string, string>,
): LayerEpoch | null {
  const epochs = series.epochs ?? [];
  if (!epochs.length) return null;
  if (isDateSeries(series)) {
    const wanted = epochOn[series.id];
    const match = wanted ? epochs.find((e) => epochDate(e) === wanted) : undefined;
    return match ?? epochs[epochs.length - 1] ?? null;
  }
  const drawn = layersOn.has(series.id)
    ? series.id
    : ([...layersOn].find((id) => epochs.some((e) => e.layer === id)) ?? null);
  const idx = epochIndexOf(series, drawn);
  return idx >= 0 ? (epochs[idx] ?? null) : null;
}

/** The years a series offers, oldest first — the slider's ticks. */
export function epochsOf(series: Layer | null): LayerEpoch[] {
  return series?.epochs ?? [];
}

/**
 * How one epoch is labelled — the slider's ticks and `#epochSelect`'s options both read this,
 * so a monthly series (e.g. a future `chirpsm` archive) cannot drift between the two controls.
 *
 * `LayerEpoch.year` is a plain integer for every series published today (`lc_annual`'s eight
 * annual rows), so this is the identity most of the time. A monthly product is expected to
 * encode the month as a fractional `YYYY.MM` (2023.05 for May 2023) rather than adding a
 * second field — the honest-label clause is about what the reader sees, not about the wire
 * shape — so a non-integer year is formatted to two decimal places instead of JS's default
 * float rendering (`2023.05`, not `2023.050000000000004`).
 *
 * **`to_date` is the one thing a bare year cannot say.** `chirpsy`'s newest epoch is the
 * calendar year so far — eight months of 2026, not twelve — and an option reading "2026"
 * beside "2025" invites exactly the comparison the data does not support. So a year the
 * server marked partial reads "2026 (to date)" / "2026 (à ce jour)". The flag is absent on
 * every finished year, so nothing else in the catalogue changes.
 *
 * Translated through `i18n.t` rather than a passed-in `t`, the way `lib/format` reaches the
 * current language: the slider (`EpochSlider`), the Layers pane select and the legend all
 * call this, and threading a translator through three call sites to reach one word would be
 * three chances for two of them to disagree.
 *
 * Task 6 adds the second, **optional** `cadence` argument for a date series — every existing
 * call site (one argument) is unaffected, because an epoch with no `date` (every legacy row)
 * always falls through to the year logic above regardless of what `cadence` says.
 *
 * * `'8d'` / `'dekad'` — a day-level date, `Intl.DateTimeFormat(lang, {day, month: 'short',
 *   year})` (`ndvi_terra`'s "Sep 6, 2026", `swi`'s ten-day dates).
 * * `'month'` — `{month: 'short', year}` (`cm_synth`'s "Sep 2026").
 * * anything else — the year logic, unchanged.
 *
 * `prelim` (a proxied series' newest, still-provisional epoch — `cm_synth`) appends
 * ` (${t('layers.prelim')})`, on top of whichever label above produced the date.
 */
export function epochLabel(epoch: LayerEpoch, cadence?: Layer['cadence']): string {
  let label: string;
  if (epoch.date && (cadence === '8d' || cadence === 'dekad' || cadence === 'month')) {
    const opts: Intl.DateTimeFormatOptions =
      cadence === 'month'
        ? { month: 'short', year: 'numeric', timeZone: 'UTC' }
        : { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' };
    label = new Intl.DateTimeFormat(i18n.language, opts).format(new Date(epoch.date));
  } else {
    const year = Number.isInteger(epoch.year) ? String(epoch.year) : epoch.year.toFixed(2);
    label = epoch.to_date ? `${year} (${i18n.t('layers.toDate')})` : year;
  }
  return epoch.prelim ? `${label} (${i18n.t('layers.prelim')})` : label;
}

/**
 * The layer id to **ask about**, for a raster that is drawn.
 *
 * A series row and its newest epoch are the same pixels — `lc_annual` borrows that year's
 * dataset, so `config.cog` resolves to the same COG — but they are not the same *row*, and
 * one difference matters: the series carries no `config.epoch`, which is the field
 * `apps.analysis.rasters.epochs_for` keys on. Asked about the series, `/analysis/insights`
 * therefore has no epoch family to walk and answers with four blocks and no temporal trend
 * — for the one dataset in the catalogue that most obviously has one.
 *
 * So the panel asks about the **year on the slider**: the drawn id where that is already an
 * epoch, and the newest published epoch where the series row itself is drawn. The numbers
 * are identical either way; what changes is that the trend block exists.
 */
export function analysisLayerFor(
  layers: readonly Layer[] | undefined,
  drawn: string | null,
): string | null {
  if (!drawn) return null;
  const series = seriesFor(layers, drawn);
  if (!series || series.id !== drawn) return drawn;
  const epochs = series.epochs ?? [];
  return epochs[epochs.length - 1]?.layer ?? drawn;
}

/**
 * The title to show for a **drawn** raster — client, 2026-09-15: "a general name - Land
 * cover, then the year can show when a user filters by year."
 *
 * `apps.datasets.serializers` already keeps the series' own `name` year-free (it cannot
 * fall through to the bound epoch's dataset title the way every other row does — see
 * `_NAME_OVERRIDE` there), so drawing the series itself already reads right. What it does
 * not fix is drawing one of its *years*: `lca2019`'s own name is genuinely "Land cover 2019
 * — annual 10 m (Impact Observatory)" — a real title for a real catalogue row — but showing
 * it where the reader has just picked 2019 off the epoch select repeats the year in a
 * second place and drops the "Land cover" the client asked for as the constant. So a drawn
 * epoch reads `${series name} · ${epoch label}` instead — "Land cover (Impact Observatory,
 * 10 m) · 2019" — the same `epochLabel` the select and the slider already use.
 *
 * `name` is the caller's own fallback (ordinarily `layer.name`) for everything this is not
 * about: a raster with no series, or a series id whose `epochs` cannot place it (empty
 * before the first harvest).
 */
export function drawnEpochTitle(
  layers: readonly Layer[] | undefined,
  drawnId: string | null,
  name: string,
): string {
  const series = seriesFor(layers, drawnId);
  if (!series) return name;
  const epoch = epochsOf(series)[epochIndexOf(series, drawnId)];
  if (!epoch) return name;
  return `${series.name} · ${epochLabel(epoch, series.cadence)}`;
}
